import React, { useState, useEffect } from 'react';
import MDButton from 'components/MDButton';
import MDModal from 'components/MDModal';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import { useAppServices, useUploadImage } from 'hook/services';
import { Button, Stack, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Editor from 'components/editor';

const EditModal = ({ data, handleRefresh, apps, category }) => {
  const AppService = useAppServices();
  const uploadImage = useUploadImage();

  const [openEditProduct, setOpenEditProduct] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [description, setDescription] = useState(data.description);

  // Separate state for existing images (URLs) and new images (File objects)
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  useEffect(() => {
    // Initialize existing images from data.library (assuming it's an array of URLs or objects with url)
    setExistingImages(
      Array.isArray(data.library)
        ? data.library.map(img => (typeof img === 'string' ? img : img.url || img))
        : []
    );
    setNewImages([]);
    setDescription(data.description);
  }, [data]);

  const handleModal = () => setOpenEditProduct(true);
  const closeEditProduct = () => setOpenEditProduct(false);

  // Handle new image selection
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setNewImages(prev => [...prev, ...files]);
  };

  // Remove existing image by index
  const handleRemoveExisting = (idx) => {
    setExistingImages(prev => prev.filter((_, i) => i !== idx));
  };

  // Remove new image by index
  const handleRemoveNew = (idx) => {
    setNewImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    // Upload new images and get their URLs
    let newImageResponses = [];
    if (newImages.length) {
      newImageResponses = await Promise.all(
        newImages.map((file) =>
          uploadImage({ file, desiredPath: `app/logo/image` })
        )
      );
    }

    // Combine existing image URLs and new uploaded image URLs
    const allImages = [
      ...existingImages,
      ...newImageResponses.map(r => r?.response?.data).filter(Boolean)
    ];

    const payload = {
      _id: data._id,
      name: e.target.name.value,
      video: e.target.video_link.value,
      description,
      path: e.target.path.value,
      library: allImages,
    };

    const { response } = await AppService.allTheAppDescription.update({ payload });

    if (response) {
      setProcessing(false);
      closeEditProduct();
      handleRefresh();
    } else {
      setProcessing(false);
    }
  };

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 612,
    maxHeight: '80vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
  };

  return (
    <>
      <MDButton color="info" variant="contained" onClick={handleModal}>
        Edit
      </MDButton>
      <MDModal open={openEditProduct} onClose={closeEditProduct}>
        <MDBox>
          <MDBox component="form" onSubmit={handleSubmit} role="form" sx={style} encType="multipart/form-data">
            <MDBox mb={2}>
              <MDTypography variant="button">Name</MDTypography>
              <MDInput label="Name" name="name" fullWidth defaultValue={data.name} />
            </MDBox>

            <MDBox mb={2}>
              <MDTypography variant="button">Description</MDTypography>
              <Editor handleChange={setDescription} data={description} />
            </MDBox>

            <MDBox mb={2}>
              <MDTypography variant="button">Path</MDTypography>
              <MDInput label="path" name="path" fullWidth defaultValue={data.path} />
            </MDBox>

            <MDBox mb={2}>
              <MDTypography variant="button">Video Link</MDTypography>
              <MDInput label="Video Link" name="video_link" fullWidth defaultValue={data.video} />
            </MDBox>

            <Stack direction="row" alignItems="center" spacing={2} style={{ marginTop: 10 }}>
              <Button variant="contained" component="label" sx={{ color: '#fff' }}>
                Upload Images +
                <input hidden multiple name="images" type="file" onChange={handleImageChange} />
              </Button>
            </Stack>

            {/* Show existing images */}
            <MDBox>
              {existingImages.map((url, idx) => (
                <MDBox
                  key={`existing-${idx}`}
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    marginTop: 2,
                    marginRight: 2,
                  }}
                >
                  <img
                    src={url}
                    alt="existing"
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      backgroundColor: 'white',
                      ':hover': { backgroundColor: 'red' },
                    }}
                    onClick={() => handleRemoveExisting(idx)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </MDBox>
              ))}
              {/* Show new images */}
              {newImages.map((file, idx) => (
                <MDBox
                  key={`new-${idx}`}
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    marginTop: 2,
                    marginRight: 2,
                  }}
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      backgroundColor: 'white',
                      ':hover': { backgroundColor: 'red' },
                    }}
                    onClick={() => handleRemoveNew(idx)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </MDBox>
              ))}
            </MDBox>

            <MDBox display="flex" justifyContent="flex-end">
              <MDButton
                variant="gradient"
                color="primary"
                type="button"
                sx={{ mt: 2, mb: 1 }}
                onClick={closeEditProduct}
              >
                Close
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                type="submit"
                sx={{ mt: 2, mb: 1, ml: 1 }}
                loading={processing}
                disabled={processing}
              >
                Save
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDModal>
    </>
  );
};

export default EditModal;
